import { inject, Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { concatMap, of, OperatorFunction } from 'rxjs';

import {
  DEFAULT_SAVE_GATE,
  SaveGateOptions,
} from '../utils/survey-save-pipeline.helper';
import { SurveySaveBuildStage } from './stages/survey-save.build.stage';
import { SurveySaveUpdateStage } from './stages/survey-save.update.stage';
import { SurveySaveValidateStage } from './stages/survey-save.validate.stage';
import {
  SurveySaveFollowUp,
  SurveyUpdateOverrides,
} from './survey-save.pipeline.types';

// Every save-* effect runs validate → build → update; each is a stage in `stages/`.
@Injectable({ providedIn: 'root' })
export class SurveySavePipeline {
  private readonly _validateStage = inject(SurveySaveValidateStage);
  private readonly _buildStage = inject(SurveySaveBuildStage);
  private readonly _updateStage = inject(SurveySaveUpdateStage);

  // For a follow-up that needs a value off the triggering action (e.g. the target buyer).
  public saveWithTrigger<TriggerAction extends Action>(
    followUpFor: (action: TriggerAction) => SurveySaveFollowUp,
  ): OperatorFunction<TriggerAction, Action> {
    return (trigger$) =>
      trigger$.pipe(
        concatMap((action) =>
          of<Action>(action).pipe(this.save(followUpFor(action))),
        ),
      );
  }

  // followUp returns trigger actions only; its HTTP stays in its own effect.
  public save(
    followUp: SurveySaveFollowUp,
    gate: SaveGateOptions = DEFAULT_SAVE_GATE,
    overrides: SurveyUpdateOverrides = {},
  ): OperatorFunction<Action, Action> {
    return (trigger$) =>
      trigger$.pipe(
        this._validateStage.validate(gate),
        this._buildStage.build(overrides),
        this._updateStage.persist(followUp),
      );
  }
}
